/**
 * @file derivedBinLayout.ts
 * @description Derived (auto) bin layout for the V3 sync: for every bin whose
 * layoutMode is an auto mode, member positions are COMPUTED from the bin's
 * childOrder + the layout engine — never read from canvasPositions — and the
 * bin auto-sizes to fit. Inside a container, arrangement is an order, not a
 * set of coordinates: concurrent edits merge as list operations and the
 * layout stays coherent. Revives the design from closed PR #1009.
 */

import type {
  Asset,
  CanvasBin,
  Job,
  SubjectCanvasNode,
  SubjectCollection,
  TextAnnotation,
  VariableCanvasNode,
  VariationStack,
} from '@/types'
import type { CanvasPosition } from '@/store/yjsStore'
import { isCameraMotionControlAsset } from '@/lib/assetUtils'
import { parseStackMemberId, toStackMemberId } from '@/lib/stackMembers'
import { resolveCollectionLayout } from '@/lib/subjectCollectionCardLayout'
import { normalizeBinLayoutMode, resolveBinLayoutWidth } from '@/components/canvas/shared/binLayoutMode'
import { computeBinCollapsedWidth, computeBinContactSheetLayout } from './nodes/binContactSheetLayout'
import type { BinPreviewItem } from './types'
import {
  computeBinLayout,
  computeInsertionIndex,
  type BinChildInfo,
  type BinLayoutResult,
} from '@/components/canvas/shared/computeBinLayout'
import { getMemberCardDimensions } from '@/components/canvas/shared/assetCardDimensions'
import {
  BIN_CONTENT_PADDING,
  BIN_ITEM_GAP,
  BIN_LABEL_BAND_HEIGHT,
  DEFAULT_BIN_HEIGHT,
  DEFAULT_BIN_WIDTH,
  STACK_BACKDROP_HEADER_HEIGHT,
  STACK_BACKDROP_PADDING_BOTTOM,
} from '@/components/canvas/shared/canvasPanelConstants'
import { computeAspectRatio } from './yjsToEngineNode'
import {
  AUDIO_NODE_HEIGHT,
  AUDIO_NODE_WIDTH,
  SET_NODE_HEIGHT,
  SET_NODE_WIDTH,
  calculateNodeDimensions,
  parseAspectRatio,
} from '@/components/canvas/shared/canvasConstants'

const SUBJECT_NODE_SIZE = 113
// Matches the rendered VariableNodeView defaults; stored dims win below.
const VARIABLE_NODE_FALLBACK = { width: 180, height: 80 }
const ANNOTATION_FALLBACK = { width: 200, height: 60 }

export interface DerivedBinLayouts {
  /** nodeId (`asset-<id>`, `job-<id>`, …) → bin-relative slot position. */
  memberPositions: Map<string, { x: number; y: number }>
  /** binId → derived size for auto-layout bins and their containing freeform ancestors. */
  binSizes: Map<string, { width: number; height: number }>
}

export interface DerivedBinLayoutInput {
  sceneId: string | null | undefined
  canvasBins: ReadonlyMap<string, CanvasBin>
  assets: ReadonlyMap<string, Asset>
  jobs: ReadonlyMap<string, Job>
  textAnnotations: ReadonlyMap<string, TextAnnotation>
  subjectCanvasNodes: ReadonlyMap<string, SubjectCanvasNode>
  variableCanvasNodes: ReadonlyMap<string, VariableCanvasNode>
  /**
   * Subject collections. Unlike other node kinds, a collection's bin membership
   * lives on its POSITION record (`canvasPositions[`collection-<id>`].parentBinId`),
   * not on the collection itself. Optional so the ONE call site can adopt it
   * incrementally; when absent, collections simply don't join auto layouts.
   */
  subjectCollections?: ReadonlyMap<string, SubjectCollection>
  /** Shared stack records, used to give a collapsed stack one stable layout slot. */
  variationStacks?: ReadonlyMap<string, VariationStack>
  canvasPositions: ReadonlyMap<string, CanvasPosition>
  /**
   * PER-CLIENT view filters. The fields below shape what THIS client renders;
   * layouts computed with them
   * must never be written into the shared doc — the canvasPositions mirror
   * derives from a canonical input instead (see useCanvasEngine).
   */
  hideFailedGenerations?: boolean
  /** Members of collapsed variation stacks (hidden behind the stack card). */
  stackedAssetIds?: ReadonlySet<string>
  /** Subjects hidden behind collapsed variation stacks. */
  stackedSubjectIds?: ReadonlySet<string>
  /** Bins currently collapsed — their children aren't rendered; skip layout. */
  collapsedBinIds?: ReadonlySet<string>
  /** Expanded stacks whose footer/backdrop extends below their member cards. */
  expandedStackIds?: ReadonlySet<string>
}

/**
 * Drop the per-client view filters from a layout input, leaving only
 * canonical (shared-doc-derived) inputs. Reorder writes build the new
 * childOrder from this so locally-hidden members survive the write.
 */
function stripPerClientFilters(input: DerivedBinLayoutInput): DerivedBinLayoutInput {
  return {
    ...input,
    hideFailedGenerations: false,
    stackedAssetIds: undefined,
    stackedSubjectIds: undefined,
    collapsedBinIds: undefined,
    expandedStackIds: undefined,
  }
}

/**
 * Stacked-member exclusion for the shared-doc mirror: canonical stack
 * MEMBERSHIP (asset/subject → live stack in the shared doc), never the
 * per-client collapse/expand state. Every client derives the same sets from
 * the same doc, so mirror writes computed with them are identical everywhere.
 */
export function computeCanonicalStackedIds(
  assets: ReadonlyMap<string, Asset>,
  subjects: ReadonlyMap<string, SubjectCanvasNode>,
  variationStacks: ReadonlyMap<string, VariationStack>,
): { stackedAssetIds: Set<string>; stackedSubjectIds: Set<string> } {
  const stackedAssetIds = new Set<string>()
  const stackedSubjectIds = new Set<string>()
  const isLiveStack = (stackId: string | undefined | null): boolean => {
    if (!stackId) return false
    const stack = variationStacks.get(stackId)
    return Boolean(stack && !stack.deletedAt)
  }
  for (const [id, asset] of assets) {
    if (isLiveStack(asset.stackId)) stackedAssetIds.add(id)
  }
  for (const [id, subject] of subjects) {
    if (isLiveStack(subject.stackId)) stackedSubjectIds.add(id)
  }
  return { stackedAssetIds, stackedSubjectIds }
}

interface MemberEntry extends BinChildInfo {
  /** Bare entity id — what childOrder stores. */
  entityId: string
  /** Stored position for the (y, x, id) fallback ordering. */
  sortY: number
  sortX: number
}

function assetDims(asset: Asset): { width: number; height: number } {
  if (asset.assetType === 'file') return { width: 240, height: 112 }
  // Camera-motion control assets render on the set node (`getAssetNodeKind` /
  // `assetToSetNode`), so they occupy set-sized slots, same as sets.
  if (asset.assetType === 'set' || isCameraMotionControlAsset(asset)) {
    return { width: SET_NODE_WIDTH, height: SET_NODE_HEIGHT }
  }
  if (asset.assetType === 'audio') return { width: AUDIO_NODE_WIDTH, height: AUDIO_NODE_HEIGHT }
  // Size the slot the way the RENDERER sizes the card: computeAspectRatio
  // prefers probed width/height over the requested aspect string. Reading the
  // string alone reserved a differently-shaped slot than the card drawn into
  // it, so the card overhung its row and pushed out through the derived bin
  // frame — members visibly "popping out" of their bin. It looked spontaneous
  // because useBackfillAspectRatios and generation completion write real
  // dimensions asynchronously, resizing the card with no user action.
  // (jobDims below still reads the requested string: a pending job has no
  // probed dimensions, and jobToEngineNode sizes it from that same string.)
  return calculateNodeDimensions(computeAspectRatio(asset))
}

function jobDims(job: Job): { width: number; height: number } {
  if (job.operation === 'i2s') return { width: SET_NODE_WIDTH, height: SET_NODE_HEIGHT }
  if (job.operation === 'generate-audio' || job.operation === 'inpaint-audio')
    return { width: AUDIO_NODE_WIDTH, height: AUDIO_NODE_HEIGHT }
  return calculateNodeDimensions(parseAspectRatio(job.generationParams?.aspectRatio))
}

/**
 * Reorder an auto-layout bin's childOrder from a same-bin drop: the dropped
 * member's new index comes from the drop point measured against the layout of
 * the REMAINING members (homescreen semantics — where you let go is where it
 * goes). The drop index is probed against the VISIBLE members (input's
 * per-client filters — the geometry the user saw during the drag preview),
 * then spliced into the full canonical member order so locally-hidden members
 * keep their childOrder places. Returns the new childOrder, or null when the
 * bin is freeform, missing, or the moved member isn't visible in it.
 */
export function computeReorderedChildOrder(
  input: DerivedBinLayoutInput,
  binId: string,
  movedEntityId: string,
  dropPosition: { x: number; y: number },
): string[] | null {
  const bin = input.canvasBins.get(binId)
  if (!bin) return null
  const mode = normalizeBinLayoutMode(bin.layoutMode)
  if (mode === 'freeform') return null

  const visible = collectOrderedBinMembers(input, binId, bin)
  const moved = visible.find(member => member.entityId === movedEntityId)
  if (!moved) return null
  const visibleRemaining = visible.filter(member => member.entityId !== movedEntityId)
  const fullRemaining = collectOrderedBinMembers(stripPerClientFilters(input), binId, bin)
    .filter(member => member.entityId !== movedEntityId)
    .map(member => member.entityId)
  if (fullRemaining.length === 0) return [movedEntityId]

  const insertAt = resolveCanonicalInsertIndex(
    visibleRemaining,
    fullRemaining,
    mode,
    bin.gridColumns,
    resolveBinLayoutWidth(bin, input.canvasBins),
    {
      x: dropPosition.x + moved.width / 2,
      y: dropPosition.y + moved.height / 2,
    },
    input,
  )
  const newOrder = [...fullRemaining]
  newOrder.splice(insertAt, 0, movedEntityId)
  return newOrder
}

/**
 * Translate a drop probe into an index in the FULL member order: probe the
 * visible layout for the insertion gap the user aimed at, then anchor on the
 * visible member that gap precedes. Hidden members keep their relative order;
 * a drop past the last visible member (or into a bin that LOOKS empty) lands
 * at the end of the flow.
 */
function resolveCanonicalInsertIndex(
  visibleRemaining: MemberEntry[],
  fullRemaining: string[],
  mode: ReturnType<typeof normalizeBinLayoutMode>,
  gridColumns: number | undefined,
  layoutWidth: number | undefined,
  probeCenter: { x: number; y: number },
  input: DerivedBinLayoutInput,
): number {
  if (visibleRemaining.length === 0) return fullRemaining.length
  const baseLayout = computeBinLayout(visibleRemaining, mode, null, null, gridColumns, layoutWidth)
  const layout = mode === 'grid' ? applyExpandedStackBottomClearance(input, visibleRemaining, baseLayout) : baseLayout
  const visibleIndex = computeInsertionIndex(probeCenter.x, probeCenter.y, visibleRemaining, layout.positions, mode)
  const anchor = visibleRemaining[visibleIndex]?.entityId
  if (!anchor) return fullRemaining.length
  const anchorIndex = fullRemaining.indexOf(anchor)
  return anchorIndex === -1 ? fullRemaining.length : anchorIndex
}

/**
 * Seat a dragged set into an auto-layout bin as ONE contiguous block at a
 * single insertion point (mirrors the collection batch drop): remaining
 * members keep their relative order and the block lands where the probe
 * points. Works for cross-bin drops too — block entities need not be current
 * members of the bin (at commit time the state snapshot still holds their old
 * membership; they are excluded from `remaining` either way). The probe is
 * measured against the VISIBLE members (per-client filters — what the user
 * saw) and spliced into the full canonical order, same as
 * `computeReorderedChildOrder`. Returns null for freeform or unknown bins.
 */
export function computeBlockReorderedChildOrder(
  input: DerivedBinLayoutInput,
  binId: string,
  blockEntityIds: string[],
  probeCenter: { x: number; y: number },
): string[] | null {
  const bin = input.canvasBins.get(binId)
  if (!bin) return null
  const mode = normalizeBinLayoutMode(bin.layoutMode)
  if (mode === 'freeform') return null
  if (blockEntityIds.length === 0) return null

  const blockSet = new Set(blockEntityIds)
  const visibleRemaining = collectOrderedBinMembers(input, binId, bin).filter(member => !blockSet.has(member.entityId))
  const fullRemaining = collectOrderedBinMembers(stripPerClientFilters(input), binId, bin)
    .filter(member => !blockSet.has(member.entityId))
    .map(member => member.entityId)
  if (fullRemaining.length === 0) return [...blockEntityIds]

  const insertAt = resolveCanonicalInsertIndex(
    visibleRemaining,
    fullRemaining,
    mode,
    bin.gridColumns,
    resolveBinLayoutWidth(bin, input.canvasBins),
    probeCenter,
    input,
  )
  const next = [...fullRemaining]
  next.splice(insertAt, 0, ...blockEntityIds)
  return next
}

/**
 * Compute slot positions and container sizes for every auto-layout bin in the
 * scene. Ordering: the bin's childOrder (bare entity ids; stale ids are inert)
 * first, then any members it doesn't mention in (y, x, id) stored-position
 * order — so legacy bins switch modes deterministically.
 */
export function computeDerivedBinLayouts(
  input: DerivedBinLayoutInput,
  options?: {
    /**
     * Bins whose size is already known (the live drag preview's reflowed
     * source/target). Their members aren't laid out; their ancestors pack
     * around the given size.
     */
    binSizeOverrides?: ReadonlyMap<string, { width: number; height: number }>
  },
): DerivedBinLayouts {
  const memberPositions = new Map<string, { x: number; y: number }>()
  const binSizes = new Map<string, { width: number; height: number }>()
  const overrides = options?.binSizeOverrides

  const { sceneId, canvasBins, collapsedBinIds } = input
  const resolved = new Set<string>()
  const visiting = new Set<string>()

  const resolveBin = (binId: string): void => {
    if (resolved.has(binId) || visiting.has(binId)) return
    const bin = canvasBins.get(binId)
    if (!bin) return
    visiting.add(binId)

    // Resolve nested bins first so a parent's auto-layout uses each child's
    // real derived dimensions instead of stale/default stored dimensions.
    for (const [childId, child] of canvasBins) {
      if (child.parentBinId === binId) resolveBin(childId)
    }

    if (sceneId && bin.sceneId !== sceneId) {
      visiting.delete(binId)
      resolved.add(binId)
      return
    }
    const override = overrides?.get(binId)
    if (override) {
      binSizes.set(binId, override)
      visiting.delete(binId)
      resolved.add(binId)
      return
    }
    if (collapsedBinIds?.has(binId)) {
      // A closed bin is a contact sheet; a parent's auto-layout must pack
      // around that footprint, not the stored open size.
      const stored = input.canvasPositions.get(`bin-${binId}`)
      const width = computeBinCollapsedWidth(stored?.width ?? DEFAULT_BIN_WIDTH)
      const sheetItems = collectSheetItems(input, binId, bin, binSizes)
      binSizes.set(binId, { width, height: computeBinContactSheetLayout(width, sheetItems).height })
      visiting.delete(binId)
      resolved.add(binId)
      return
    }
    const mode = normalizeBinLayoutMode(bin.layoutMode)
    if (mode === 'freeform') {
      // A derived child can grow after upload/generation completes. Carry its
      // footprint through freeform ancestors too; the canonical mirror persists
      // this growth without changing any manually placed member coordinates.
      const stored = input.canvasPositions.get(`bin-${binId}`)
      let width = stored?.width ?? DEFAULT_BIN_WIDTH
      let height = stored?.height ?? DEFAULT_BIN_HEIGHT
      let containsDerivedChild = false
      for (const [childId, child] of canvasBins) {
        if (child.parentBinId !== binId || child.sceneId !== bin.sceneId) continue
        const childSize = binSizes.get(childId)
        const childPosition = input.canvasPositions.get(`bin-${childId}`)
        if (!childSize || !childPosition) continue
        containsDerivedChild = true
        width = Math.max(width, childPosition.x + childSize.width + BIN_CONTENT_PADDING)
        height = Math.max(height, childPosition.y + childSize.height + BIN_LABEL_BAND_HEIGHT + BIN_CONTENT_PADDING)
      }
      if (containsDerivedChild) {
        binSizes.set(binId, { width, height })
      }
    } else {
      const ordered = collectOrderedBinMembers(input, binId, bin, binSizes)
      const stored = input.canvasPositions.get(`bin-${binId}`)
      if (ordered.length === 0 && bin.preserveEmptySize && stored?.width && stored.height) {
        // A workflow's empty outputs keep the saved frame instead of shrinking
        // to an empty grid. Both render and canonical mirror use this rule.
        binSizes.set(binId, { width: stored.width, height: stored.height })
      } else if (ordered.length > 0 || resolveBinLayoutWidth(bin, input.canvasBins)) {
        const baseLayout = computeBinLayout(
          ordered,
          mode,
          null,
          null,
          bin.gridColumns,
          resolveBinLayoutWidth(bin, input.canvasBins),
        )
        const layout = mode === 'grid' ? applyExpandedStackBottomClearance(input, ordered, baseLayout) : baseLayout
        for (const [nodeId, slot] of layout.positions) memberPositions.set(nodeId, slot)
        binSizes.set(binId, { width: layout.width, height: layout.height })
      }
    }

    visiting.delete(binId)
    resolved.add(binId)
  }

  for (const binId of canvasBins.keys()) resolveBin(binId)

  return { memberPositions, binSizes }
}

/**
 * Expanded stack chrome is taller than the member cards that drive grid
 * layout: its name/footer band and lower backdrop padding render beneath the
 * last member. Reserve that rendered footprint before placing the next grid
 * item, and include it when the stack is the final item in the bin.
 */
function applyExpandedStackBottomClearance(
  input: DerivedBinLayoutInput,
  ordered: MemberEntry[],
  layout: BinLayoutResult,
): BinLayoutResult {
  if (!input.expandedStackIds || input.expandedStackIds.size === 0) return layout

  const groups = new Map<string, Array<{ nodeId: string; index: number }>>()
  for (let index = 0; index < ordered.length; index++) {
    const nodeId = ordered[index].id
    const parsed = parseStackMemberId(nodeId)
    if (!parsed) continue
    const stackId =
      parsed.kind === 'asset' ? input.assets.get(parsed.id)?.stackId : input.subjectCanvasNodes.get(parsed.id)?.stackId
    if (!stackId || !input.expandedStackIds.has(stackId)) continue
    const group = groups.get(stackId) ?? []
    group.push({ nodeId, index })
    groups.set(stackId, group)
  }
  if (groups.size === 0) return layout

  const positions = new Map(layout.positions)
  const orderedGroups = [...groups.values()].sort(
    (a, b) => Math.max(...a.map(member => member.index)) - Math.max(...b.map(member => member.index)),
  )
  const stackBottom = (group: Array<{ nodeId: string; index: number }>): number => {
    let memberBottom = 0
    for (const { nodeId } of group) {
      const position = positions.get(nodeId)
      if (!position) continue
      const dims = getMemberCardDimensions(nodeId, input.assets, input.subjectCanvasNodes)
      memberBottom = Math.max(memberBottom, position.y + dims.thumbnailHeight)
    }
    return memberBottom + STACK_BACKDROP_PADDING_BOTTOM + STACK_BACKDROP_HEADER_HEIGHT
  }

  for (const group of orderedGroups) {
    const lastMemberIndex = Math.max(...group.map(member => member.index))
    const successorIndexes = ordered
      .map((member, index) => ({ nodeId: member.id, index }))
      .filter(member => member.index > lastMemberIndex && positions.has(member.nodeId))
    if (successorIndexes.length === 0) continue

    const nextTop = Math.min(...successorIndexes.map(member => positions.get(member.nodeId)!.y))
    const shiftY = stackBottom(group) + BIN_ITEM_GAP - nextTop
    if (shiftY <= 0) continue
    for (const { nodeId } of successorIndexes) {
      const position = positions.get(nodeId)!
      positions.set(nodeId, { ...position, y: position.y + shiftY })
    }
  }

  let contentBottom = 0
  for (const member of ordered) {
    const position = positions.get(member.id)
    if (position) contentBottom = Math.max(contentBottom, position.y + member.height)
  }
  for (const group of orderedGroups) contentBottom = Math.max(contentBottom, stackBottom(group))

  return {
    ...layout,
    positions,
    height: Math.max(layout.height, contentBottom + BIN_CONTENT_PADDING),
  }
}

/**
 * Freeze an auto-layout bin's arrangement for the switch to freeform. Members
 * visible under the input's per-client filters keep the exact slots they held
 * on screen (a layout prefix is stable, so laying out [visible..., hidden...]
 * reproduces the visible slots), and locally-hidden members (failed while
 * hiding, collapsed-stack members) are appended after them through the same
 * layout engine — placed, not stacked on top of visible cards. Returns null
 * for freeform/unknown bins or empty bins without a derived size.
 */
export function computeMaterializedBinLayout(
  input: DerivedBinLayoutInput,
  binId: string,
): { memberPositions: Map<string, { x: number; y: number }>; size: { width: number; height: number } } | null {
  const bin = input.canvasBins.get(binId)
  if (!bin) return null
  const mode = normalizeBinLayoutMode(bin.layoutMode)
  if (mode === 'freeform') return null

  const visible = collectOrderedBinMembers(input, binId, bin)
  const visibleIds = new Set(visible.map(member => member.entityId))
  const hidden = collectOrderedBinMembers(stripPerClientFilters(input), binId, bin).filter(
    member => !visibleIds.has(member.entityId),
  )
  const ordered = [...visible, ...hidden]
  if (ordered.length === 0) {
    // Persist empty-bin width edits in the user's transaction too. Otherwise
    // the untracked geometry mirror replaces the saved size and undo loses it.
    const size = computeDerivedBinLayouts({ ...input, collapsedBinIds: undefined }).binSizes.get(binId)
    return size ? { memberPositions: new Map(), size } : null
  }

  const baseLayout = computeBinLayout(
    ordered,
    mode,
    null,
    null,
    bin.gridColumns,
    resolveBinLayoutWidth(bin, input.canvasBins),
  )
  const layout = mode === 'grid' ? applyExpandedStackBottomClearance(input, ordered, baseLayout) : baseLayout
  return { memberPositions: layout.positions, size: { width: layout.width, height: layout.height } }
}

/**
 * Gather a bin's visible members with rendered dims, ordered by childOrder
 * (stale ids inert) then stored (y, x, id) for anything unlisted. Exported so
 * the live drag-gap preview (binDragPreviewController) gathers members exactly
 * like the sync + drop paths — same order, same dims, same filters — instead of
 * duplicating the gathering.
 */
/**
 * The closed-bin contact-sheet model for `binId`: one entry per member, with
 * nested bins carrying their own children recursively. Only kinds matter here
 * (the sheet's height is what the layout needs), so every non-bin member is a
 * plain cell.
 */
function collectSheetItems(
  input: DerivedBinLayoutInput,
  binId: string,
  bin: CanvasBin,
  derivedBinSizes?: ReadonlyMap<string, { width: number; height: number }>,
): BinPreviewItem[] {
  return collectOrderedBinMembers(input, binId, bin, derivedBinSizes).map(member => {
    if (!member.id.startsWith('bin-')) return { kind: 'image' as const }
    const child = input.canvasBins.get(member.entityId)
    return {
      kind: 'bin' as const,
      name: child?.name,
      children: child ? collectSheetItems(input, member.entityId, child, derivedBinSizes) : [],
    }
  })
}

export function collectOrderedBinMembers(
  input: DerivedBinLayoutInput,
  binId: string,
  bin: CanvasBin,
  derivedBinSizes?: ReadonlyMap<string, { width: number; height: number }>,
): MemberEntry[] {
  const {
    sceneId,
    canvasBins,
    assets,
    jobs,
    textAnnotations,
    subjectCanvasNodes,
    variableCanvasNodes,
    subjectCollections,
    variationStacks,
    canvasPositions,
    hideFailedGenerations,
    stackedAssetIds,
    stackedSubjectIds,
  } = input

  const members: MemberEntry[] = []
  const push = (
    entityId: string,
    nodeId: string,
    dims: { width: number; height: number },
    sortPosition = canvasPositions.get(nodeId),
  ): void => {
    members.push({
      id: nodeId,
      entityId,
      width: dims.width,
      height: dims.height,
      sortY: sortPosition?.y ?? 0,
      sortX: sortPosition?.x ?? 0,
    })
  }

  type CollapsedStackGroup = {
    stackId: string
    assets: Asset[]
    subjects: SubjectCanvasNode[]
    memberNodeIds: string[]
  }
  const collapsedStackGroups = new Map<string, CollapsedStackGroup>()
  const addCollapsedStackMember = (stackId: string, memberNodeId: string, member: Asset | SubjectCanvasNode): void => {
    let group = collapsedStackGroups.get(stackId)
    if (!group) {
      group = { stackId, assets: [], subjects: [], memberNodeIds: [] }
      collapsedStackGroups.set(stackId, group)
    }
    group.memberNodeIds.push(memberNodeId)
    if (memberNodeId.startsWith('asset-')) group.assets.push(member as Asset)
    else group.subjects.push(member as SubjectCanvasNode)
  }

  for (const [id, asset] of assets) {
    if (asset.binId !== binId) continue
    if (sceneId && asset.sceneId !== sceneId) continue
    if (hideFailedGenerations && asset.status === 'failed') continue
    if (asset.versionGroupId && asset.isActiveVersion === false) continue
    if (stackedAssetIds?.has(id)) {
      if (asset.stackId) addCollapsedStackMember(asset.stackId, `asset-${id}`, asset)
      continue
    }
    push(id, `asset-${id}`, assetDims(asset))
  }
  for (const [id, job] of jobs) {
    if (job.binId !== binId) continue
    if (sceneId && job.sceneId !== sceneId) continue
    if (job.status !== 'pending' && job.status !== 'running' && job.status !== 'failed') continue
    if (hideFailedGenerations && job.status === 'failed') continue
    push(id, `job-${id}`, jobDims(job))
  }
  for (const [id, annotation] of textAnnotations) {
    if (annotation.binId !== binId) continue
    if (sceneId && annotation.sceneId !== sceneId) continue
    const stored = canvasPositions.get(`annotation-${id}`)
    push(id, `annotation-${id}`, {
      width: annotation.width ?? stored?.width ?? ANNOTATION_FALLBACK.width,
      height: stored?.height ?? ANNOTATION_FALLBACK.height,
    })
  }
  for (const [id, subject] of subjectCanvasNodes) {
    if (subject.binId !== binId) continue
    if (subject.collectionId) continue
    if (sceneId && subject.sceneId !== sceneId) continue
    if (stackedSubjectIds?.has(id)) {
      if (subject.stackId) addCollapsedStackMember(subject.stackId, `subject-${id}`, subject)
      continue
    }
    push(id, `subject-${id}`, { width: SUBJECT_NODE_SIZE, height: SUBJECT_NODE_SIZE })
  }
  for (const group of collapsedStackGroups.values()) {
    const stack = variationStacks?.get(group.stackId)
    const stackOrder = new Map<string, number>()
    for (const [index, rawMemberId] of (stack?.memberOrder ?? []).entries()) {
      const parsed = parseStackMemberId(rawMemberId)
      if (parsed) stackOrder.set(toStackMemberId(parsed), index)
    }
    const childOrder = new Map((bin.childOrder ?? []).map((entityId, index) => [entityId, index]))
    const orderedMemberNodeIds = [...group.memberNodeIds].sort((a, b) => {
      const aEntityId = a.slice(a.indexOf('-') + 1)
      const bEntityId = b.slice(b.indexOf('-') + 1)
      const aChild = childOrder.get(aEntityId) ?? Number.POSITIVE_INFINITY
      const bChild = childOrder.get(bEntityId) ?? Number.POSITIVE_INFINITY
      if (aChild !== bChild) return aChild - bChild
      const aStack = stackOrder.get(a) ?? Number.POSITIVE_INFINITY
      const bStack = stackOrder.get(b) ?? Number.POSITIVE_INFINITY
      if (aStack !== bStack) return aStack - bStack
      return a.localeCompare(b)
    })
    const anchorNodeId = orderedMemberNodeIds[0]
    if (!anchorNodeId) continue
    const anchorEntityId = anchorNodeId.slice(anchorNodeId.indexOf('-') + 1)

    const orderedAssets = [...group.assets].sort((a, b) => {
      const ai = stackOrder.get(`asset-${a.id}`) ?? Number.POSITIVE_INFINITY
      const bi = stackOrder.get(`asset-${b.id}`) ?? Number.POSITIVE_INFINITY
      if (ai === bi) return (a.shotNumber ?? 0) - (b.shotNumber ?? 0)
      return ai - bi
    })
    const representativeAsset = orderedAssets.find(asset => asset.status === 'completed') ?? orderedAssets[0]
    const dims = calculateNodeDimensions(representativeAsset ? computeAspectRatio(representativeAsset) : 16 / 9)
    const sortPosition =
      canvasPositions.get(`stack-${group.stackId}`) ??
      orderedMemberNodeIds.map(nodeId => canvasPositions.get(nodeId)).find(position => position !== undefined)
    push(anchorEntityId, `stack-${group.stackId}`, dims, sortPosition)
  }
  for (const [id, variable] of variableCanvasNodes) {
    if (variable.binId !== binId) continue
    if (sceneId && variable.sceneId !== sceneId) continue
    const stored = canvasPositions.get(`variable-${id}`)
    push(id, `variable-${id}`, {
      width: stored?.width ?? VARIABLE_NODE_FALLBACK.width,
      height: stored?.height ?? VARIABLE_NODE_FALLBACK.height,
    })
  }
  if (subjectCollections) {
    for (const [id, collection] of subjectCollections) {
      // Membership is on the position record, and the bin is already scene-
      // gated above, so parentBinId === binId is the whole test.
      const pos = canvasPositions.get(`collection-${id}`)
      if (pos?.parentBinId !== binId) continue
      const { width, height } = resolveCollectionLayout({
        variantCount: collection.memberOrder?.length ?? 0,
        persistedWidth: pos.width,
        persistedHeight: pos.height,
      })
      push(id, `collection-${id}`, { width, height })
    }
  }
  for (const [id, childBin] of canvasBins) {
    if (childBin.parentBinId !== binId) continue
    if (sceneId && childBin.sceneId !== sceneId) continue
    const stored = canvasPositions.get(`bin-${id}`)
    const derived = derivedBinSizes?.get(id)
    // A bin's name hangs below its body; reserve that band in the parent's
    // layout so the next row never covers it.
    push(id, `bin-${id}`, {
      width: derived?.width ?? stored?.width ?? DEFAULT_BIN_WIDTH,
      height: (derived?.height ?? stored?.height ?? DEFAULT_BIN_HEIGHT) + BIN_LABEL_BAND_HEIGHT,
    })
  }

  const byEntityId = new Map(members.map(m => [m.entityId, m]))
  const ordered: MemberEntry[] = []
  for (const entityId of bin.childOrder ?? []) {
    const member = byEntityId.get(entityId)
    if (member) {
      ordered.push(member)
      byEntityId.delete(entityId)
    }
  }
  const leftovers = [...byEntityId.values()].sort(
    (a, b) => a.sortY - b.sortY || a.sortX - b.sortX || a.id.localeCompare(b.id),
  )
  ordered.push(...leftovers)
  return ordered
}
